import { createFileRoute } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { DocumentHead } from '@/components/core/DocumentHead';
import { IconExternalLink } from '@/icons';
import { CenteredColumn } from '@/components/CenteredColumn';
import Button from '@/components/core/Button';
import { useFavicon } from '@/hooks/useFavicon';
import { useBranding } from '@/hooks/useBranding';

export const Route = createFileRoute('/accessibility')({
  component: AccessibilityStatement,
});

function AccessibilityStatement() {
  const { formatMessage } = useIntl();
  const branding = useBranding();
  useFavicon();

  return (
    <>
      <DocumentHead
        titleKey="page.accessibilityStatement.title"
        descriptionKey="page.accessibilityStatement.description"
      />
      <CenteredColumn>
        <h1>
          <FormattedMessage
            defaultMessage="Tillgänglighets­redogörelse för {serviceName}"
            values={{ serviceName: branding?.serviceName }}
          />
        </h1>
        <Button variant="outline" to="/">
          <FormattedMessage defaultMessage="Till startsidan" />
        </Button>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Om webbplatsen" />
          </h2>
          <p>
            <FormattedMessage
              defaultMessage="{serviceName} är en webbaserad tjänst för hantering av kollektivtrafikbiljetter. Tjänsten möjliggör administration av resenärer, inköp av biljetter, och hantering av avdelningar och kostnadsställen."
              values={{ serviceName: branding?.serviceName }}
            />
          </p>
          <p>
            <strong>
              <FormattedMessage defaultMessage="Webbplatsen ägs och utvecklas av:" />
            </strong>{' '}
            Mobilix AB
          </p>
          <p>
            <strong>
              <FormattedMessage defaultMessage="Webbadress:" />
            </strong>{' '}
            <TextLink href="https://mobilix.io" text="https://mobilix.io" />
          </p>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Om denna tillgänglighetsredogörelse" />
          </h2>
          <p>
            <FormattedMessage defaultMessage="Mobilix är ansvarig för denna webbplats och vill att så många som möjligt ska kunna använda den. Den här tillgänglighetsredogörelsen gäller AllTix-portalen och beskriver hur väl webbplatsen är förenlig med lagen om tillgänglighet till digital offentlig service (DOS-lagen)." />
          </p>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Överensstämmelse med DOS-lagen" />
          </h2>
          <p>
            <FormattedMessage defaultMessage="Denna webbplats är förenlig med lagen om tillgänglighet till digital offentlig service (DOS-lagen) och uppfyller WCAG 2.2 Nivå AA." />
          </p>
          <p>
            <FormattedMessage defaultMessage="Vi har genomfört en omfattande tillgänglighetsrevision och åtgärdat identifierade brister. Webbplatsen är fullt navigerbar med tangentbord, kompatibel med skärmläsare, och följer moderna tillgänglighetsstandarder. Automatiserad testning med axe DevTools visar inga tillgänglighetsbrister." />
          </p>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Kända brister" />
          </h2>
          <p>
            <FormattedMessage defaultMessage="Inga kända tillgänglighetsbrister har identifierats vid den senaste revisionen, annat än att vissa kunder har bett om att använda färgteman som inte uppfyller WCAG 2.2 Nivå AA-kraven." />
          </p>

          <h3>
            <FormattedMessage defaultMessage="Genomförda förbättringar" />
          </h3>
          <p>
            <FormattedMessage defaultMessage="Följande tillgänglighetsförbättringar har genomförts och verifierats:" />
          </p>
          <ul>
            <li>
              <FormattedMessage defaultMessage="✓ Förbättrad navigeringsstruktur med korrekta ARIA-attribut och semantiska element" />
            </li>
            <li>
              <FormattedMessage defaultMessage="✓ Alla sökfält och autocomplete-komponenter har fullständiga ARIA-attribut" />
            </li>
            <li>
              <FormattedMessage defaultMessage="✓ Obligatoriska formulärfält meddelas korrekt till skärmläsare med aria-required" />
            </li>
            <li>
              <FormattedMessage defaultMessage="✓ Externa länkar meddelar att de öppnas i ny flik" />
            </li>
            <li>
              <FormattedMessage defaultMessage="✓ Alla bilder har beskrivande alternativtext eller är korrekt markerade som dekorativa" />
            </li>
            <li>
              <FormattedMessage defaultMessage="✓ Systemmeddelanden (toasts) använder korrekta ARIA-roller för skärmläsare" />
            </li>
            <li>
              <FormattedMessage defaultMessage="✓ Rubrikhierarki är korrekt strukturerad på alla sidor" />
            </li>
            <li>
              <FormattedMessage defaultMessage="✓ Färgkontraster uppfyller WCAG 2.2 Nivå AA-kraven (4.5:1 för normal text, 3:1 för stora texter och UI-komponenter)" />
            </li>
          </ul>

          <h3>
            <FormattedMessage defaultMessage="Kontinuerlig förbättring" />
          </h3>
          <p>
            <FormattedMessage defaultMessage="Vi fortsätter att övervaka och förbättra webbplatsens tillgänglighet. Vi utför regelbundna tester med automatiserade verktyg och skärmläsare, samt inhämtar återkoppling från användare." />
          </p>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Tillgängliga alternativ" />
          </h2>
          <p>
            <FormattedMessage defaultMessage="Om du inte kan använda webbplatsen på grund av bristande tillgänglighet kan du kontakta oss för att få hjälp på ett alternativt sätt. Vi kan bistå med att:" />
          </p>
          <ul className="list-inside list-disc">
            <li>
              <FormattedMessage defaultMessage="Tillhandahålla information i alternativt format" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Hjälpa till med specifika funktioner via telefon eller e-post" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Ge teknisk support för att underlätta användning med hjälpmedel" />
            </li>
          </ul>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Meddela brister i tillgänglighet" />
          </h2>
          <p>
            <FormattedMessage defaultMessage="Om du upptäcker brister i webbplatsens tillgänglighet eller om du har behov av innehåll som är undantaget från lagens tillämpningsområde, kan du kontakta oss. Vi tar tacksamt emot all feedback och arbetar för att åtgärda problemen så snart som möjligt." />
          </p>

          <h3>
            <FormattedMessage defaultMessage="Kontakta oss" />
          </h3>
          <ul className="list-inside list-disc">
            <li>
              <strong>
                <FormattedMessage defaultMessage="E-post:" />
              </strong>{' '}
              <a
                href="mailto:support@mobilix.io"
                className="text-primary-600 hover:text-primary-700 hover:underline"
              >
                support@mobilix.io
              </a>
            </li>
          </ul>
          <p>
            <FormattedMessage defaultMessage="Vi strävar efter att svara på din anmälan inom 2 veckor." />
          </p>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Tillsyn och tillsynsmyndighet" />
          </h2>
          <p>
            <FormattedMessage defaultMessage="Myndigheten för digital förvaltning (DIGG) har ansvaret för tillsyn över lagen om tillgänglighet till digital offentlig service." />
          </p>
          <p>
            <FormattedMessage defaultMessage="Om du inte är nöjd med hur vi hanterat dina synpunkter eller om du behöver mer hjälp kan du kontakta DIGG och anmäla bristande tillgänglighet:" />
          </p>
          <TextLink
            href="https://www.digg.se/tdosanmalan"
            text={formatMessage({
              defaultMessage: 'Anmäl bristande tillgänglighet till DIGG',
            })}
          />
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Teknisk information" />
          </h2>

          <h3>
            <FormattedMessage defaultMessage="Utvärderingsmetod" />
          </h3>
          <p>
            <FormattedMessage defaultMessage="Vi har använt följande metoder för att bedöma tillgängligheten:" />
          </p>
          <ul className="list-inside list-disc">
            <li>
              <FormattedMessage defaultMessage="Självbedömning utförd av utvecklingsteamet" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Manuell kodgranskning av komponenter och funktionalitet" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Granskning mot WCAG 2.2 success criteria" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Tangentbordsnavigering utan mus" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Test med skärmläsare (NVDA, VoiceOver)" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Automatiserade verktyg som komplement (axe DevTools, WAVE)" />
            </li>
          </ul>

          <h3>
            <FormattedMessage defaultMessage="Standarder" />
          </h3>
          <p>
            <FormattedMessage defaultMessage="Webbplatsen strävar efter att uppfylla följande standarder:" />
          </p>
          <ul className="list-inside list-disc">
            <li>
              <FormattedMessage defaultMessage="EN 301 549 - Tillgänglighetskrav för IKT-produkter och IKT-tjänster" />
            </li>
            <li>
              <FormattedMessage defaultMessage="WCAG 2.2 (Web Content Accessibility Guidelines) Nivå AA" />
            </li>
            <li>
              <FormattedMessage defaultMessage="WAI-ARIA 1.2 (Web Accessibility Initiative - Accessible Rich Internet Applications)" />
            </li>
          </ul>

          <h3>
            <FormattedMessage defaultMessage="Tillgänglighetsfunktioner" />
          </h3>
          <ul className="list-inside list-disc">
            <li>
              <FormattedMessage defaultMessage="Fullt navigerbar med tangentbord" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Synliga fokusindikatorer på alla interaktiva element" />
            </li>
            <li>
              <FormattedMessage defaultMessage="'Hoppa till huvudinnehåll'-länk" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Semantiska landmärken (header, nav, main, aside)" />
            </li>
            <li>
              <FormattedMessage defaultMessage="ARIA-attribut för formulär och interaktiva komponenter" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Stöd för prefers-reduced-motion (reducerad rörelse)" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Responsiv design som fungerar vid 200% zoom" />
            </li>
          </ul>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="När gjordes bedömningen?" />
          </h2>
          <p>
            <FormattedMessage
              defaultMessage="Tillgänglighetsredogörelsen är baserad på en självbedömning utförd av utvecklingsteamet och uppdaterades den {date}."
              values={{ date: '25 mars 2026' }}
            />
          </p>
          <p>
            <FormattedMessage defaultMessage="Vi granskar och uppdaterar denna redogörelse löpande, minst en gång per år, eller när större ändringar görs i tjänsten." />
          </p>
        </section>

        <section>
          <h2>
            <FormattedMessage defaultMessage="Mer information om tillgänglighet" />
          </h2>
          <p>
            <FormattedMessage defaultMessage="För mer information om tillgänglighetskrav och riktlinjer, se:" />
          </p>
          <ul className="mb-0">
            <li>
              <TextLink
                href="https://www.digg.se/kunskap-och-stod/digital-tillganglighet"
                text={formatMessage({
                  defaultMessage: 'Myndigheten för digital förvaltning (DIGG)',
                })}
              />
            </li>
            <li>
              <TextLink
                href="https://www.w3.org/WAI/WCAG22/quickref/"
                text={formatMessage({
                  defaultMessage: 'WCAG 2.2 Quick Reference',
                })}
              />
            </li>
            <li>
              <TextLink
                href="https://www.w3.org/WAI/ARIA/apg/"
                text={formatMessage({
                  defaultMessage: 'WAI-ARIA Authoring Practices Guide',
                })}
              />
            </li>
            <li>
              <TextLink
                href="https://webbriktlinjer.se/"
                text={formatMessage({
                  defaultMessage: 'Webbriktlinjer (Sverige)',
                })}
              />
            </li>
          </ul>
        </section>
      </CenteredColumn>
    </>
  );
}

const TextLink = ({ href, text }: { href: string; text: string }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${text} (öppnas i ny flik)`}
      className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600"
    >
      {text}
      <IconExternalLink className="size-4" aria-hidden="true" />
    </a>
  );
};
